import { Trans, t } from "@lingui/macro";
import { AriaButtonProps } from "@react-types/button";
import { AnimatePresence, motion } from "framer-motion";
import Image, { type StaticImageData } from "next/image";
import React, { ReactNode, useMemo, useRef, useState } from "react";
import { useButton, useId } from "react-aria";
import styled from "styled-components";
import { Color } from "~/guidelines/Color";
import mediaQueries, { useIsLgScreen } from "~/guidelines/Theme/mediaQueries";
import { body1, heading2, heading6 } from "~/guidelines/Typography";
import CosmeticsImage from "~/applications/OfferSearch/Ui/OfferDetails/SleepingEquipmentSection/Cosmetics.jpg";
import MattressImage from "~/applications/OfferSearch/Ui/OfferDetails/SleepingEquipmentSection/Mattress.jpg";
import PillowsImage from "~/applications/OfferSearch/Ui/OfferDetails/SleepingEquipmentSection/Pillows.jpg";
import SheetImage from "~/applications/OfferSearch/Ui/OfferDetails/SleepingEquipmentSection/Sheet.jpg";

const Container = styled.div`
  background-color: ${({ theme }) => theme.color.background.darkFirst};
  padding: ${({ theme }) => theme.spacing(7, 5)};
  ${mediaQueries.lg()} {
    padding: 0;
  }
`;

const Inner = styled.div`
  display: flex;
  flex-direction: row-reverse;

  ${mediaQueries.lg()} {
    max-width: 1240px;
    margin: 0 auto;
    padding: ${({ theme }) => theme.spacing(18, 0)};
    gap: ${({ theme }) => theme.spacing(16)};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(8)};

  ${mediaQueries.lg()} {
    gap: ${({ theme }) => theme.spacing(0)};
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};
`;

const Title = styled.div`
  ${heading2};
  color: ${({ theme }) => theme.color.text.darkFirst};

  & em {
    font-style: normal;
    color: ${({ theme }) => theme.color.text.lightTertiary};
  }
`;

const Description = styled.div`
  ${body1};
  color: ${({ theme }) => theme.color.text.darkFirst};

  ${mediaQueries.lg()} {
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 6px;
  overflow: hidden;
`;

const Preview = styled.div`
  display: none;

  ${mediaQueries.lg()} {
    display: block;
    width: 100%;
    max-width: 610px;
  }
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const Item = styled.div<{ isSelected: boolean }>`
  padding: ${({ theme }) => theme.spacing(5, 0)};
  ${heading6};
  color: ${({ theme }) => theme.color.text.darkFirst};
  border-bottom: 1px solid white;

  &:last-of-type {
    border-bottom: none;
  }

  ${mediaQueries.lg()} {
    cursor: pointer;
    color: ${({ theme, isSelected = false }) =>
      isSelected
        ? Color.fromString(theme.color.text.darkFirst).opacity(1).rgba()
        : Color.fromString(theme.color.text.darkFirst).opacity(0.3).rgba()};

    &:hover {
      color: ${({ theme }) =>
        Color.fromString(theme.color.text.darkFirst).opacity(1).rgba()};
    }

    transition: color 0.2s ease-in-out;
  }
`;

type Perk = {
  key: string;
  title: string;
  image: StaticImageData;
  style?: React.CSSProperties;
};

const PerkItem = ({
  isSelected,
  ...props
}: AriaButtonProps<"div"> & { isSelected: boolean; children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { buttonProps } = useButton(props, ref);

  return (
    <Item ref={ref} {...buttonProps} isSelected={isSelected}>
      {props.children}
    </Item>
  );
};

// eslint-disable-next-line @typescript-eslint/ban-types
type SleepingEquipmentSectionProps = {};

// eslint-disable-next-line no-empty-pattern
const SleepingEquipmentSection = ({}: SleepingEquipmentSectionProps) => {
  const previewId = useId("perk-control");

  const perks: Array<Perk> = useMemo(
    () => [
      {
        key: "mattress",
        title: t`High-quality bedding, selected for its comfort and lasting quality`,
        image: MattressImage,
      },
      {
        key: "sheets-and-towels",
        title: t`100% cotton sheets and bath linen, french-made`,
        image: SheetImage,
      },
      {
        key: "pillows-and-duvet",
        title: t`OEKO-TEX certified pillows and duvets`,
        image: PillowsImage,
      },
      {
        key: "cosmetics",
        title: t`Cosmetics formulated with natural ingredients`,
        image: CosmeticsImage,
        style: { objectFit: "contain" },
      },
    ],
    [],
  );

  const [selectedItem, setSelectedItem] = useState<Perk>(perks[0]);
  const isLgScreen = useIsLgScreen();

  return (
    <Container>
      <Inner>
        <Content>
          <Header>
            <Title>
              <Trans>
                We are professionals in <em>French</em> hospitality
              </Trans>
            </Title>

            <Description>
              <Trans>
                Take advantage of the best facilities and the full extent of
                French know-how in your accommodation:
              </Trans>
            </Description>
          </Header>

          <ItemList>
            {perks.map((onePerks) => (
              <PerkItem
                key={onePerks.key}
                isSelected={onePerks.key === selectedItem.key}
                isDisabled={!isLgScreen}
                aria-controls={previewId}
                onPress={() => setSelectedItem(onePerks)}
              >
                {onePerks.title}
              </PerkItem>
            ))}
          </ItemList>
        </Content>

        <Preview>
          <ImageContainer>
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedItem.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{ width: "100%", height: "100%", position: "relative" }}
              >
                <Image
                  id={previewId}
                  src={selectedItem.image}
                  fill
                  style={selectedItem.style ?? { objectFit: "cover" }}
                  alt=""
                />
              </motion.div>
            </AnimatePresence>
          </ImageContainer>
        </Preview>
      </Inner>
    </Container>
  );
};

export default SleepingEquipmentSection;
